import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft, Scissors } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { pic } from "@/lib/pictures";

const QR_OPTS = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [365, "1 year"], [0, "No end date"]];
const until = (q) => (q?.expires_at ? `Works until ${new Date(q.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : "No end date");

const QrBlock = ({ k, onChange }) => {
  const set = async (days) => {
    if (!window.confirm("Make a new QR code? The old printed card's QR code will stop working.")) return;
    try { onChange(k.id, (await http.put(`/family/children/${k.id}/qr`, { days })).data); toast.success("New QR code made. Print the new card."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex items-center gap-4 mt-4" data-testid={`login-card-qr-${k.id}`}>
      <QRCodeSVG value={`${window.location.origin}/kids?qr=${k.qr.token}`} size={92} fgColor="#2C4A3E" />
      <div className="text-xs text-stone-600 space-y-1">
        <p><b className="text-[#2C4A3E]">Scan to sign in</b>, then tap my 3 pictures.</p>
        <p data-testid={`login-card-qr-until-${k.id}`}>{until(k.qr)}</p>
        <select value="" onChange={(e) => e.target.value !== "" && set(Number(e.target.value))} data-testid={`login-card-qr-days-${k.id}`} className="no-print mt-1 px-2 py-1 rounded-lg border border-[#E6DEC3] bg-white text-xs">
          <option value="">QR works for… (makes a new code)</option>
          {QR_OPTS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
        </select>
      </div>
    </div>
  );
};

const Card = ({ k, code, onQr }) => (
  <div className="login-card rounded-3xl border-2 border-dashed border-[#2C4A3E]/40 bg-white p-6 break-inside-avoid" data-testid={`login-card-${k.id}`}>
    <p className="text-[10px] uppercase tracking-[0.25em] text-[#C87D53] font-semibold">ConsciousLearn · My login card</p>
    <p className="font-display text-3xl text-[#2C4A3E] mt-2" data-testid={`login-card-name-${k.id}`}>{k.name.split(" ")[0]}</p>
    <ol className="mt-4 space-y-3 text-sm text-stone-700">
      <li><span className="text-stone-500">1. Go to</span> <b className="text-[#2C4A3E]">consciouslearn.com/kids</b></li>
      <li><span className="text-stone-500">2. Family code</span> <b className="ml-1 text-xl tracking-[0.3em] text-[#2C4A3E]" data-testid={`login-card-code-${k.id}`}>{code}</b></li>
      <li><span className="text-stone-500">3. Tap my name</span> <b className="ml-1 text-[#2C4A3E]" data-testid={`login-card-username-${k.id}`}>{k.username}</b></li>
      <li><span className="text-stone-500">4. Tap my pictures in order</span></li>
    </ol>
    <div className="mt-3 flex gap-3" data-testid={`login-card-pictures-${k.id}`}>
      {[0, 1, 2].map((n) => {
        const p = k.pictures && pic(k.pictures[n]);
        return (
          <div key={n} className="relative w-20 h-20 rounded-2xl border-2 border-[#E6DEC3] grid place-items-center bg-[#FDFBF7]">
            <span className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-[#2C4A3E] text-white text-xs grid place-items-center">{n + 1}</span>
            {p ? <><p.Icon className="w-10 h-10" style={{ color: p.color }} /><span className="sr-only">{p.label}</span></> : <span className="text-stone-300 text-2xl">?</span>}
          </div>
        );
      })}
    </div>
    {k.qr && <QrBlock k={k} onChange={onQr} />}
    {!k.pictures && <p className="mt-3 text-xs text-[#C87D53] no-print" data-testid={`login-card-reset-${k.id}`}>Pictures were set before cards existed. Re-save this child's picture login on the dashboard to print them, or draw them in.</p>}
    <p className="mt-4 text-[11px] text-stone-400">Keep this card at home. Don't share your pictures with friends.</p>
  </div>
);

export default function LoginCards() {
  const [q] = useSearchParams();
  const [d, setD] = useState(null);
  useEffect(() => { http.get("/family/kid-login").then((r) => setD(r.data)); }, []);
  const only = q.get("kid");
  const kids = (d?.kids || []).filter((k) => k.has_pictures && (!only || k.id === only));
  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="login-cards-page">
      <div className="no-print flex flex-wrap items-center gap-3 mb-8">
        <Link to="/parent" data-testid="login-cards-back" className="inline-flex items-center gap-1.5 text-sm text-stone-500"><ArrowLeft className="w-4 h-4" />My kids</Link>
        <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] w-full">Printable login cards</h1>
        <p className="text-sm text-stone-500 flex items-center gap-2"><Scissors className="w-4 h-4" />Print, cut along the dotted line, and stick it near your kid's device.</p>
        <button onClick={() => window.print()} disabled={!kids.length} data-testid="login-cards-print" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm disabled:opacity-50"><Printer className="w-4 h-4" />Print</button>
        {only && <Link to="/parent/login-cards" data-testid="login-cards-all" className="text-sm underline text-[#2C4A3E]">Show all kids</Link>}
      </div>
      {d && !kids.length && <p className="text-stone-500" data-testid="login-cards-empty">No kids have a picture login yet. Set one up on your dashboard first.</p>}
      <div className="grid sm:grid-cols-2 gap-6">{kids.map((k) => <Card key={k.id} k={k} code={d.family_code} onQr={(id, qr) => setD({ ...d, kids: d.kids.map((x) => (x.id === id ? { ...x, qr } : x)) })} />)}</div>
    </div>
  );
}
